import React, { useMemo } from "react";
import { Callout } from "../../../ui";
import { AddressBookDrawerProps } from "./types";
import { getLocationString } from "./utils";

const AddressBookDrawer = React.memo<AddressBookDrawerProps>(
  ({
    open,
    onClose,
    drawerType,
    contacts,
    isLoading,
    error,
    searchQuery,
    onSearchChange,
    onAddressSelect,
    isContactValidForSource,
    isContactValidForDestination,
    allowedOriginCountry,
    allowedDestinationCountry,
  }) => {
    const filteredContacts = useMemo(
      () =>
        contacts.filter(
          (contact) =>
            contact.nickname
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase()) ||
            contact.contactName
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase()) ||
            getLocationString(contact.city)
              ?.toLowerCase()
              .includes(searchQuery?.toLowerCase()) ||
            (contact.companyName &&
              contact.companyName
                ?.toLowerCase()
                .includes(searchQuery?.toLowerCase()))
        ),
      [contacts, searchQuery]
    );

    const validContactsCount = useMemo(
      () =>
        filteredContacts.filter((contact) =>
          drawerType === "source"
            ? isContactValidForSource(contact)
            : isContactValidForDestination(contact)
        ).length,
      [filteredContacts, drawerType, isContactValidForSource, isContactValidForDestination]
    );

    if (!open) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 1200,
          }}
        />

        {/* Drawer panel */}
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(520px, 100vw)",
            background: "var(--bg-elev)",
            zIndex: 1201,
            display: "flex",
            flexDirection: "column",
            boxShadow: "-8px 0 40px rgba(0,0,0,0.15)",
            borderRadius: "24px 0 0 24px",
          }}
        >
          <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                marginBottom: 24,
              }}
            >
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "var(--ink)" }}>
                  Address Book
                </h2>
                <p style={{ margin: "0 0 4px", fontSize: 14, color: "var(--ink-3)" }}>
                  Select a saved address for{" "}
                  {drawerType === "source" ? "ship from" : "deliver to"}
                </p>
                {filteredContacts.length > 0 && (
                  <p style={{ margin: 0, fontSize: 12, color: "var(--ink-4)" }}>
                    {validContactsCount} of {filteredContacts.length} addresses available for{" "}
                    {drawerType === "source" ? allowedOriginCountry : allowedDestinationCountry}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(220,38,38,0.1)",
                  color: "var(--err)",
                  cursor: "pointer",
                  fontSize: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginLeft: 12,
                }}
                aria-label="Close address book"
              >
                ×
              </button>
            </div>

            {/* Search */}
            <div style={{ marginBottom: 20 }}>
              <input
                type="text"
                placeholder="Search addresses..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 14px",
                  border: "1px solid var(--line)",
                  borderRadius: 10,
                  fontSize: 14,
                  background: "var(--bg-tint)",
                  color: "var(--ink)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      border: "3px solid var(--line)",
                      borderTopColor: "var(--brand-blue)",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : error ? (
                <Callout tone="warn">Error loading addresses. Please try again.</Callout>
              ) : filteredContacts.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 0" }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink-3)", marginBottom: 6 }}>
                    {searchQuery ? "No addresses found" : "No saved addresses"}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-4)" }}>
                    {searchQuery ? "Try a different search term" : "Add addresses to see them here"}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {filteredContacts.map((contact) => {
                    const isValid =
                      drawerType === "source"
                        ? isContactValidForSource(contact)
                        : isContactValidForDestination(contact);

                    return (
                      <button
                        key={contact.id}
                        type="button"
                        disabled={!isValid}
                        onClick={() => {
                          if (!isValid) return;
                          onAddressSelect(contact);
                        }}
                        style={{
                          width: "100%",
                          padding: 16,
                          border: `2px solid ${isValid ? "rgba(43,85,255,0.2)" : "rgba(220,38,38,0.3)"}`,
                          borderRadius: 12,
                          background: isValid
                            ? "rgba(43,85,255,0.02)"
                            : "rgba(220,38,38,0.05)",
                          cursor: isValid ? "pointer" : "not-allowed",
                          textAlign: "left",
                          opacity: isValid ? 1 : 0.65,
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (isValid) {
                            (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--brand-blue)";
                            (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)";
                            (e.currentTarget as HTMLButtonElement).style.boxShadow =
                              "0 8px 24px rgba(43,85,255,0.12)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isValid) {
                            (e.currentTarget as HTMLButtonElement).style.borderColor =
                              "rgba(43,85,255,0.2)";
                            (e.currentTarget as HTMLButtonElement).style.transform = "none";
                            (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
                          }
                        }}
                      >
                        {/* Contact header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8,
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)" }}>
                            {contact.nickname || contact.contactName}
                          </span>
                          <div style={{ display: "flex", gap: 6 }}>
                            {contact.isDefault && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: "2px 8px",
                                  borderRadius: 10,
                                  background: "var(--ok-bg)",
                                  color: "var(--ok)",
                                  border: "1px solid var(--ok)",
                                }}
                              >
                                Default
                              </span>
                            )}
                            {contact.isFavorite && (
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: "2px 8px",
                                  borderRadius: 10,
                                  background: "rgba(43,85,255,0.08)",
                                  color: "var(--brand-blue)",
                                  border: "1px solid rgba(43,85,255,0.2)",
                                }}
                              >
                                Favorite
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Contact details */}
                        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", marginBottom: 4 }}>
                          {contact.contactName}
                          {contact.companyName && ` • ${contact.companyName}`}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5 }}>
                          <div>{contact.street}, {contact.houseNo}</div>
                          <div>
                            {getLocationString(contact.city)},{" "}
                            {getLocationString(contact.state)} {contact.zipCode}
                          </div>
                          <div>{getLocationString(contact.country)}</div>
                        </div>

                        {/* Invalid warning */}
                        {!isValid && (
                          <div
                            style={{
                              marginTop: 10,
                              padding: "8px 12px",
                              background: "rgba(220,38,38,0.08)",
                              border: "1px solid rgba(220,38,38,0.2)",
                              borderRadius: 6,
                              fontSize: 12,
                              color: "var(--err)",
                              fontWeight: 500,
                            }}
                          >
                            Cannot be used for {drawerType === "source" ? "ship from" : "deliver to"}.
                            Required country:{" "}
                            {drawerType === "source" ? allowedOriginCountry : allowedDestinationCountry}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

AddressBookDrawer.displayName = "AddressBookDrawer";

export default AddressBookDrawer;
